import { useState } from 'react';
import { Form } from '@bancobarigui/barihaus';

import { useErrorHandler } from '@/shared/hooks/useErrorHandler';

export interface AttachmentFormData {
  description: string;
  file: File | null;
}

export interface AttachmentData {
  id: string;
  description: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  filePath: string;
  file: File;
}

interface UseAttachmentModalProps {
  onClose: () => void;
  onSubmit: (data: AttachmentData) => void;
}

export function useAttachmentModal({ onSubmit, onClose }: UseAttachmentModalProps) {
  const [form] = Form.useForm();
  const { handleError } = useErrorHandler();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const maxSize = 10 * 1024 * 1024;

      if (file.size > maxSize) {
        handleError(new Error('Arquivo muito grande'), 'O arquivo deve ter no máximo 10MB');
        event.target.value = '';
        return;
      }

      setSelectedFile(file);
      form.setFieldValue('file', file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    form.setFieldValue('file', null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleFormSubmit = async (values: AttachmentFormData) => {
    if (!selectedFile) {
      handleError(new Error('Arquivo não selecionado'), 'Selecione um arquivo para anexar');
      return;
    }

    try {
      const attachmentData: AttachmentData = {
        id: Math.random().toString(36).substr(2, 9),
        description: values.description,
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        fileType: selectedFile.type || 'application/octet-stream',
        filePath: '',
        file: selectedFile,
      };

      onSubmit(attachmentData);
      form.resetFields();
      setSelectedFile(null);
      onClose();
    } catch (error: any) {
      handleError(error, 'Erro ao adicionar anexo');
    }
  };

  return {
    form,
    selectedFile,
    handleFileChange,
    handleRemoveFile,
    handleFormSubmit,
    formatFileSize,
  };
}
